import axios from "axios";
import { useAuthStore } from "@/stores/auth.store";
import { env } from "./env";

// ── CSRF helper ──────────────────────────────────────────────────────────────
// Read the csrf_token double-submit cookie (set by the server on login — it is
// NOT HttpOnly so JS can read it). Returns an empty string when not present
// (unauthenticated requests, demo mode, or server-side rendered environments
// where document is unavailable).
function getCsrfToken(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

export const api = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: env.VITE_API_TIMEOUT,
  // Must be true so browsers send auth cookies (and receive Set-Cookie headers)
  // on cross-origin requests (e.g. localhost:5173 → localhost:4001).
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token refresh queue to prevent concurrent refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve();
    }
  });
  failedQueue = [];
}

// Request interceptor:
//   - In demo mode: attach the fake Bearer token from the store (the demo
//     server does not support cookies).
//   - In production: tokens are in HttpOnly cookies sent automatically by the
//     browser; no Authorization header is needed. Add the CSRF double-submit
//     header on mutating requests so the server can verify it.
api.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();

  if (accessToken && accessToken !== "demo-access-token" && accessToken !== "") {
    // Demo mode or explicit token override — send as Bearer.
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    // Production cookie mode — add CSRF header on state-changing requests.
    const method = (config.method ?? "").toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers["X-CSRF-Token"] = csrf;
      }
    }
  }

  return config;
});

// Response interceptor: on 401, attempt a silent token refresh (browser sends
// refresh_token cookie automatically). On success, retry the original request.
// On failure, clear local auth state so the user lands on the login page.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Demo mode: if using a fake token that expired, just log out — there is
    // no real refresh endpoint to call.
    const { accessToken } = useAuthStore.getState();
    if (accessToken === "demo-access-token") {
      useAuthStore.getState().logout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => api(originalRequest))
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // POST /auth/refresh — the browser sends the refresh_token cookie
      // automatically. No body needed. withCredentials is set on the instance
      // so new cookies (rotated tokens) are also accepted.
      await axios.post(
        `${env.VITE_API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      processQueue(null);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      useAuthStore.getState().logout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
